import { useState, useEffect } from "react";
import classes from "./ProfileEdit.module.css";
import CommonFooter from "../CommonFooter/CommonFooter";
import InputField from "../ui/InputField/InputField";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { env } from "../../constants/env";
import { useAuth } from "../../context/AuthContext";
import { Bounce, toast } from "react-toastify";
import FileUpload from "../ui/FileUpload/FileUpload";
import { formatInputDate } from "../../utils/functions";

const zambiaLocations = {
  Central: ["Kabwe", "Kapiri Mposhi", "Mkushi"],
  Copperbelt: ["Ndola", "Kitwe", "Chingola", "Mufulira"],
  Eastern: ["Chipata", "Katete", "Petauke"],
  Luapula: ["Mansa", "Samfya"],
  Lusaka: ["Lusaka", "Chongwe", "Kafue"],
  Muchinga: ["Chinsali", "Isoka"],
  Northern: ["Kasama", "Mbala"],
  "North-Western": ["Solwezi", "Zambezi"],
  Southern: ["Livingstone", "Choma", "Mazabuka"],
  Western: ["Mongu", "Senanga"],
};

const EditProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedFile, setSelectedFile] = useState(null); // main file
  const [staffLicenseFile, setStaffLicenseFile] = useState(null); // staff license

  const [currentSet, setCurrentSet] = useState(0);
  const [formData, setFormData] = useState({
    labName: "",
    hpczLicense: "",
    province: "",
    city: "",
    telephone: "",
    email: "",
    fax: "",
    physicalAddress: "",
    tierLevel: "",
    type: "",
    affiliation: "",
    personnelName: "",
    personnelPhone: "",
    personnelEmail: "",
    qualification: "",
    degreeHolders: 0,
    diplomaHolders: 0,
    certificateHolders: 0,
    dataClerks: 0,
    phlebotomists: 0,
    cleaners: 0,
    cleanersDedicated: "",
    drivers: 0,
    driversDedicated: "",
    degreeHoldersAdequate: "",
    diplomaHoldersAdequate: "",
    certificateHoldersAdequate: "",
    dataClerksAdequate: "",
    phlebotomistsAdequate: "",
    cleanersAdequate: "",
    cleanersTrained: "",
    driversAdequate: "",
    driversTrained: "",
    others: 0,
    othersAdequate: "",
    status: "", // New dropdown field
    slipta: {
      lastCertified: "",
      nextDue: "",
    },
    sadcas: {
      type: "",
      lastAssessment: "",
      nextDue: "",
    },
    hpcz: {
      licenseNumber: "",
      expiryDate: "",
      validityPeriod: "",
    },
  });

  const [prefilledFields, setPrefilledFields] = useState({
    labName: false,
    hpczLicense: false,
  });

  const [isUpdated, setIsUpdated] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const isEditMode = !!labId; // true if editing, false if creating

  console.log(formData);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchData = async () => {
      try {
        const res = await fetch(`${env.kataApiUrl}/api/profile/${labId}`);
        const data = await res.json();
        if (res.ok) {
          setFormData({
            ...formData, // keep defaults
            ...data,
            slipta: {
              lastCertified: formatInputDate(data.slipta?.lastCertified),
              nextDue: formatInputDate(data.slipta?.nextDue),
            },
            sadcas: {
              type: data.sadcas?.type || "",
              lastAssessment: formatInputDate(data.sadcas?.lastAssessment),
              nextDue: formatInputDate(data.sadcas?.nextDue),
            },
            hpcz: {
              licenseNumber: data.hpcz?.licenseNumber || "",
              expiryDate: formatInputDate(data.hpcz?.expiryDate),
              validityPeriod: data.hpcz?.validityPeriod || "",
            },
          });
          setPrefilledFields({
            labName: !!data.labName,
            hpczLicense: !!data.hpczLicense,
          });
        }
      } catch (err) {
        console.error("Failed to fetch profile data", err);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Check if it's a nested field like slipta.lastCertified or sadcas.type
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handleDone = async () => {
    setIsUpdating(true);
    try {
      const form = new FormData();

      // Append all formData fields
      for (const key in formData) {
        if (typeof formData[key] === "object" && formData[key] !== null) {
          // Flatten nested objects (like slipta, sadcas)
          for (const subKey in formData[key]) {
            form.append(`${key}[${subKey}]`, formData[key][subKey]);
          }
        } else {
          form.append(key, formData[key]);
        }
      }

      // Append file if selected
      if (selectedFile) {
        form.append("files", selectedFile); // backend saves as fileUrl
      }

      if (staffLicenseFile) {
        form.append("staffLicenses", staffLicenseFile); // backend saves as staffLicensesUrl
      }

      const res = await fetch(
        `${env.kataApiUrl}/api/profile${isEditMode ? `/${labId}` : ""}`,
        {
          method: isEditMode ? "PUT" : "POST",
          body: form, // no Content-Type header! browser sets it automatically
        }
      );

      const result = await res.json();
      if (res.ok) {
        toast.success(
          isEditMode
            ? "Profile updated successfully!"
            : "Profile created successfully!"
        );
        navigate(-1);
      } else {
        toast.error(result.error || "Operation failed.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong.");
    } finally {
      setIsUpdating(false);
    }
  };

  const baseQuestionSets = [
    {
      title: "Laboratory information",
      content: (
        <div className="d-flex flex-column gap-4">
          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Laboratory name"
                name="labName"
                placeholder="Enter name"
                value={formData.labName}
                onChange={handleChange}
                // disabled={user?.role === "LABORATORY"}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Laboratory HPCZ license number"
                name="hpczLicense"
                placeholder="License number"
                value={formData.hpczLicense}
                onChange={handleChange}
                // disabled={user?.role === "LABORATORY"}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Laboratory province"
                name="province"
                placeholder="Select province"
                dropdownOptions={Object.keys(zambiaLocations).map((prov) => ({
                  label: prov,
                  value: prov,
                }))}
                value={formData.province}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="City/Town"
                name="city"
                placeholder={
                  formData.province
                    ? "Select city/town"
                    : "Select province first"
                }
                dropdownOptions={
                  formData.province
                    ? zambiaLocations[formData.province].map((city) => ({
                        label: city,
                        value: city,
                      }))
                    : null // important: pass null to completely hide the dropdown
                }
                value={formData.city}
                onChange={handleChange}
                disabled={!formData.province} // optional: visually disable input
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Laboratory telephone"
                name="telephone"
                placeholder="Enter phone"
                value={formData.telephone}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Laboratory email"
                name="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Laboratory fax"
                name="fax"
                placeholder="Enter fax"
                value={formData.fax}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Physical address"
                name="physicalAddress"
                placeholder="Enter physical address"
                value={formData.physicalAddress}
                onChange={handleChange}
              />
            </div>
          </div>
          <InputField
            labelText="Laboratory tier level"
            name="tierLevel"
            placeholder="Select laboratory tier"
            dropdownOptions={[
              { label: "I", value: "I" },
              { label: "II", value: "II" },
              { label: "III", value: "III" },
              { label: "IV", value: "IV" },
              { label: "V", value: "V" },
              { label: "Health center", value: "Health center" },
              { label: "Other", value: "Other" },
            ]}
            value={formData.tierLevel}
            onChange={handleChange}
          />
          <InputField
            labelText="Laboratory type"
            name="type"
            placeholder="Select laboratory type"
            dropdownOptions={[
              { label: "National", value: "National" },
              { label: "Reference", value: "Reference" },
              { label: "Provincial", value: "Provincial" },
              { label: "District", value: "District" },
              { label: "Zonal", value: "Zonal" },
              { label: "Field", value: "Field" },
              { label: "Other", value: "Other" },
            ]}
            value={formData.type}
            onChange={handleChange}
          />
          <InputField
            labelText="Laboratory affiliation"
            name="affiliation"
            placeholder="Select laboratory affiliation"
            dropdownOptions={[
              { label: "Public", value: "Public" },
              { label: "Private", value: "Private" },
              { label: "Faith-Based", value: "Faith-Based" },
              { label: "Military", value: "Military" },
              { label: "Research", value: "Research" },
              { label: "Other", value: "Other" },
            ]}
            value={formData.affiliation}
            onChange={handleChange}
          />
          <div className="row">
            <div className="col-6">
              <FileUpload
                onFileSelect={(file) => setSelectedFile(file)}
                label="Upload HPCZ license"
                buttonText="Upload license"
              />
              {/* Show existing file if in edit mode */}
              {isEditMode && formData.fileUrl && (
                <div className="mt-2">
                  <a
                    href={formData.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.downloadButton}
                  >
                    View existing license
                  </a>
                </div>
              )}
            </div>

            <div className="col-6">
              <FileUpload
                label="Upload staff licenses"
                buttonText="Upload licenses"
                onFileSelect={(file) => setStaffLicenseFile(file)}
              />
              {isEditMode && formData.staffLicensesUrl && (
                <div className="mt-2">
                  <a
                    href={formData.staffLicensesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.downloadButton}
                  >
                    View existing staff licenses
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Personnel incharge",
      content: (
        <div className="d-flex flex-column gap-4">
          <InputField
            labelText="Personnel name"
            name="personnelName"
            placeholder="Enter name"
            value={formData.personnelName}
            onChange={handleChange}
          />
          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Laboratory telephone"
                name="personnelPhone"
                placeholder="Enter phone"
                value={formData.personnelPhone}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Laboratory email"
                name="personnelEmail"
                placeholder="Enter email address"
                value={formData.personnelEmail}
                onChange={handleChange}
              />
            </div>
          </div>
          <InputField
            labelText="Qualification"
            name="qualification"
            placeholder="Enter qualification"
            value={formData.qualification}
            onChange={handleChange}
          />
        </div>
      ),
    },
    {
      title: "Personnel incharge",
      content: (
        <div className="d-flex flex-column gap-4">
          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Full time degree holders"
                name="degreeHolders"
                placeholder=""
                type="number"
                value={formData.degreeHolders}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Full time diploma holders"
                name="diplomaHolders"
                placeholder=""
                type="number"
                value={formData.diplomaHolders}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Full time certificate holders"
                name="certificateHolders"
                placeholder=""
                type="number"
                value={formData.certificateHolders}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Full time data clerks"
                name="dataClerks"
                placeholder=""
                type="number"
                value={formData.dataClerks}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Full time phlebotomists"
                name="phlebotomists"
                placeholder=""
                type="number"
                value={formData.phlebotomists}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Full time  cleaners"
                name="cleaners"
                placeholder=""
                type="number"
                value={formData.cleaners}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Are lab cleaners dedicated?"
                name="cleanersDedicated"
                placeholder="Select option"
                value={formData.cleanersDedicated}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Full time drivers"
                name="drivers"
                placeholder=""
                type="number"
                value={formData.drivers}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Are lab drivers dedicated?"
                name="driversDedicated"
                placeholder="Select option"
                value={formData.driversDedicated}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Are degree holders adequate?"
                name="degreeHoldersAdequate"
                placeholder="Select option"
                value={formData.degreeHoldersAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Are diploma holders adequate?"
                name="diplomaHoldersAdequate"
                placeholder="Select option"
                value={formData.diplomaHoldersAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Are certificate holders adequate?"
                name="certificateHoldersAdequate"
                placeholder="Select option"
                value={formData.certificateHoldersAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Are  data clerks  adequate?"
                name="dataClerksAdequate"
                placeholder="Select option"
                value={formData.dataClerksAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Are phlebotomists adequate?"
                name="phlebotomistsAdequate"
                placeholder="Select option"
                value={formData.phlebotomistsAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Are cleaners adequate?"
                name="cleanersAdequate"
                placeholder="Select option"
                value={formData.cleanersAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Are cleaners trained?"
                name="cleanersTrained"
                placeholder="Select option"
                value={formData.cleanersTrained}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Are drivers adequate?"
                name="driversAdequate"
                placeholder="Select option"
                value={formData.driversAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Are drivers trained?"
                name="driversTrained"
                placeholder="Select option"
                value={formData.driversTrained}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <InputField
                labelText="Full time others"
                name="others"
                placeholder=""
                type="number"
                value={formData.others}
                onChange={handleChange}
              />
            </div>
            <div className="col-6">
              <InputField
                labelText="Are others adequate?"
                name="othersAdequate"
                placeholder="Select option"
                value={formData.othersAdequate}
                onChange={handleChange}
                dropdownOptions={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
              />
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Status & Certifications (for ASSESSOR / OWNER only)
  const statusCertQuestionSet = {
    title: "Laboratory Status & Certifications",
    content: (
      <div className="d-flex flex-column gap-4">
        <InputField
          labelText="Laboratory status"
          name="status"
          placeholder="Select status"
          value={formData.status}
          onChange={handleChange}
          dropdownOptions={[
            { label: "Poor", value: "Poor" },
            { label: "Fair", value: "Fair" },
            { label: "Good", value: "Good" },
            { label: "Excellent", value: "Excellent" },
          ]}
        />

        {/* SLIPTA */}
        <h5>SLIPTA Certification</h5>
        <div className="row">
          <div className="col-6">
            <InputField
              labelText="Last Certified"
              name="slipta.lastCertified"
              type="date"
              value={formData.slipta.lastCertified}
              onChange={handleChange}
            />
          </div>
          <div className="col-6">
            <InputField
              labelText="Next Due"
              name="slipta.nextDue"
              type="date"
              value={formData.slipta.nextDue}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* SADCAS */}
        <h5>SADCAS Assessment</h5>
        <div className="row">
          <div className="col-4">
            <InputField
              labelText="Type"
              name="sadcas.type"
              placeholder="Select SADCAS type"
              value={formData.sadcas.type}
              onChange={handleChange}
              dropdownOptions={[
                { label: "SADCAS-A", value: "SADCAS-A" },
                { label: "SADCAS-B", value: "SADCAS-B" },
                { label: "BOTH SADCAS A & B", value: "BOTH SADCAS A & B" },
              ]}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Last Assessment"
              name="sadcas.lastAssessment"
              type="date"
              value={formData.sadcas.lastAssessment}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Next Due"
              name="sadcas.nextDue"
              type="date"
              value={formData.sadcas.nextDue}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
    ),
  };

  const ownerQuestionSet = {
    title: "Laboratory Status & Certifications",
    content: (
      <div className="d-flex flex-column gap-4">
        <InputField
          labelText="Laboratory status"
          name="status"
          placeholder="Select status"
          value={formData.status}
          onChange={handleChange}
          dropdownOptions={[
            { label: "Poor", value: "Poor" },
            { label: "Fair", value: "Fair" },
            { label: "Good", value: "Good" },
            { label: "Excellent", value: "Excellent" },
          ]}
        />

        {/* SLIPTA */}
        <h5>SLIPTA Certification</h5>
        <div className="row">
          <div className="col-6">
            <InputField
              labelText="Last Certified"
              name="slipta.lastCertified"
              type="date"
              value={formData.slipta.lastCertified}
              onChange={handleChange}
            />
          </div>
          <div className="col-6">
            <InputField
              labelText="Next Due"
              name="slipta.nextDue"
              type="date"
              value={formData.slipta.nextDue}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* SADCAS */}
        <h5>SADCAS Assessment</h5>
        <div className="row">
          <div className="col-4">
            <InputField
              labelText="Type"
              name="sadcas.type"
              placeholder="Select SADCAS type"
              value={formData.sadcas.type}
              onChange={handleChange}
              dropdownOptions={[
                { label: "SADCAS-A", value: "SADCAS-A" },
                { label: "SADCAS-B", value: "SADCAS-B" },
                { label: "BOTH SADCAS A & B", value: "BOTH SADCAS A & B" },
              ]}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Last Assessment"
              name="sadcas.lastAssessment"
              type="date"
              value={formData.sadcas.lastAssessment}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Next Due"
              name="sadcas.nextDue"
              type="date"
              value={formData.sadcas.nextDue}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* HPCZ */}
        <h5>HPCZ Certification</h5>
        <div className="row">
          <div className="col-4">
            <InputField
              labelText="License Number"
              name="hpcz.licenseNumber"
              placeholder="Enter license number"
              value={formData.hpcz?.licenseNumber || ""}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Expiry Date"
              name="hpcz.expiryDate"
              type="date"
              value={formData.hpcz?.expiryDate || ""}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Validity Period"
              name="hpcz.validityPeriod"
              placeholder="e.g. 2023-2026 or 3 years"
              value={formData.hpcz?.validityPeriod || ""}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
    ),
  };


  const HPCZCertification = {
    title: "HPCZ Certification",
    content: (
      <div className="d-flex flex-column gap-4">
        {/* HPCZ */}
        {/* <h5>HPCZ Certification</h5> */}
        <div className="row">
          <div className="col-4">
            <InputField
              labelText="License Number"
              name="hpcz.licenseNumber"
              placeholder="Enter license number"
              value={formData.hpcz?.licenseNumber || ""}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Expiry Date"
              name="hpcz.expiryDate"
              type="date"
              value={formData.hpcz?.expiryDate || ""}
              onChange={handleChange}
            />
          </div>
          <div className="col-4">
            <InputField
              labelText="Validity Period"
              name="hpcz.validityPeriod"
              placeholder="e.g. 2023-2026 or 3 years"
              value={formData.hpcz?.validityPeriod || ""}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
    ),
  };

  // Conditionally assign questionSets based on user role
  let questionSets = [];

  if (user?.role === "LABORATORY" || user?.role === "MINISTRY" || user?.role === "HEALTH CENTER") {
    questionSets = [...baseQuestionSets, HPCZCertification];
  } else if (user?.role === "ASSESSOR") {
    questionSets = [statusCertQuestionSet];
  } else if (user?.role === "OWNER") {
    questionSets = [...baseQuestionSets, ownerQuestionSet];
  }

  const { title, content } = questionSets[currentSet];

  return (
    <div
      className={`${classes.wrapper} ${paramLabId ? classes.fromParams : ""}`}
    >
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">
          {isEditMode ? "Edit laboratory" : "Create laboratory"}
        </h2>
      </div>
      <div className={classes.questionContainer}>
        <h2>{title}</h2>
        {content}
      </div>
      <CommonFooter
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        cancelText="Cancel"
        handleCancel={() => navigate(-1)}
        handleDone={handleDone}
        questionSets={questionSets}
        currentSet={currentSet}
        doneText={isUpdating ? "Updating..." : "Update"} // show loading text
        nextText="Next"
        disabled={isUpdating}
      />
    </div>
  );
};

export default EditProfile;
